import React, { useState, useEffect, useCallback } from 'react';
import { X, Eye, Users, Bell, BellOff, CheckCircle2, XCircle, Shield, Loader2, AlertTriangle } from 'lucide-react';
import { toast } from '../ui/simple-toaster';
import { api } from '../../utils/api';

const ConsentManagementModal = ({ show, onClose, driverId }) => {
  const [activeShares, setActiveShares] = useState([]);
  const [endedShares, setEndedShares] = useState([]);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tablesMissing, setTablesMissing] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api('/group-view/requests/incoming');
      const requests = res.requests || [];
      setPendingRequests(requests.filter(r => r.status === 'PENDING'));

      // Load shares where this driver is shared
      const sharesRes = await api('/group-view/my-shares');
      setActiveShares((sharesRes.shares || []).filter(s => s.status === 'ACTIVE'));
      setEndedShares((sharesRes.shares || []).filter(s => s.status === 'ENDED'));
      setTablesMissing(sharesRes.tables_missing || false);
    } catch (err) {
      if (err.message?.includes('tables') || err.message?.includes('does not exist')) {
        setTablesMissing(true);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (show) loadData();
  }, [show, loadData]);

  const respondToRequest = async (requestId, status) => {
    try {
      await api(`/group-view/requests/${requestId}/respond`, {
        method: 'POST',
        body: JSON.stringify({ status }),
      });
      toast.success(status === 'APPROVED' ? 'Sharing approved' : 'Request declined');
      loadData();
    } catch (err) {
      toast.error('Failed: ' + err.message);
    }
  };

  const revokeShare = async (shareId) => {
    try {
      await api(`/group-view/shares/${shareId}/revoke`, { method: 'POST' });
      toast.success('Sharing revoked — consent ended');
      loadData();
    } catch (err) {
      toast.error('Failed: ' + err.message);
    }
  };

  if (!show) return null;

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="w-full max-w-md bg-slate-800 rounded-2xl border border-slate-700 max-h-[80vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
        data-testid="consent-management-modal"
      >
        {/* Header */}
        <div className="sticky top-0 bg-slate-800 px-4 py-3 border-b border-slate-700 flex items-center justify-between rounded-t-2xl">
          <div className="flex items-center gap-2">
            <Eye className="w-5 h-5 text-violet-400" />
            <h2 className="text-white font-medium">Group View Sharing</h2>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white" data-testid="close-consent-modal">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {/* Info Banner */}
          <div className="bg-violet-500/10 border border-violet-500/20 rounded-xl p-3 text-xs text-violet-200">
            <Shield className="w-4 h-4 inline mr-1.5 -mt-0.5 text-violet-400" />
            Group Viewers can see your compliance state and work/rest history. You control who has access.
          </div>

          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-5 h-5 text-violet-400 animate-spin" />
            </div>
          ) : tablesMissing ? (
            <div className="text-center py-6 text-slate-500 text-sm">
              <Eye className="w-8 h-8 mx-auto mb-2 opacity-30" />
              <p>Group View not yet enabled</p>
              <p className="text-xs mt-1">No one has requested to view your data</p>
            </div>
          ) : (
            <>
              {/* Pending Requests */}
              {pendingRequests.length > 0 && (
                <div>
                  <h3 className="text-xs text-amber-400 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                    <AlertTriangle className="w-3.5 h-3.5" />
                    Pending Requests ({pendingRequests.length})
                  </h3>
                  <div className="space-y-2">
                    {pendingRequests.map(req => (
                      <div key={req.request_id} className="bg-amber-500/5 border border-amber-500/20 rounded-xl p-3" data-testid={`pending-request-${req.request_id}`}>
                        <div className="flex items-center justify-between mb-2">
                          <div>
                            <span className="text-white text-sm font-medium">{req.requester_name || 'Unknown'}</span>
                            <span className="text-slate-500 text-xs ml-2">wants to view your data</span>
                          </div>
                        </div>
                        {req.message && (
                          <p className="text-xs text-slate-400 italic mb-2">{req.message}</p>
                        )}
                        <div className="flex gap-2">
                          <button
                            onClick={() => respondToRequest(req.request_id, 'APPROVED')}
                            className="flex-1 py-1.5 bg-emerald-600 text-white text-xs rounded-lg hover:bg-emerald-500 flex items-center justify-center gap-1"
                            data-testid={`approve-request-${req.request_id}`}
                          >
                            <CheckCircle2 className="w-3.5 h-3.5" /> Approve
                          </button>
                          <button
                            onClick={() => respondToRequest(req.request_id, 'DECLINED')}
                            className="flex-1 py-1.5 bg-slate-700 text-slate-300 text-xs rounded-lg hover:bg-red-500/20 hover:text-red-400 flex items-center justify-center gap-1"
                            data-testid={`decline-request-${req.request_id}`}
                          >
                            <XCircle className="w-3.5 h-3.5" /> Decline
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Active Shares */}
              <div>
                <h3 className="text-xs text-slate-400 uppercase tracking-wider mb-2">
                  Active Sharing ({activeShares.length})
                </h3>
                {activeShares.length === 0 ? (
                  <div className="text-center py-4 text-slate-600 text-xs">
                    No one is currently viewing your data
                  </div>
                ) : (
                  <div className="space-y-2">
                    {activeShares.map(share => (
                      <div key={share.share_id} className="bg-slate-700/30 border border-slate-700 rounded-xl p-3 flex items-center justify-between" data-testid={`active-share-${share.share_id}`}>
                        <div>
                          <span className="text-white text-sm">{share.viewer_name || 'Unknown'}</span>
                          <span className="text-slate-500 text-[10px] ml-2">
                            since {new Date(share.consented_at).toLocaleDateString('en-AU')}
                          </span>
                        </div>
                        <button
                          onClick={() => revokeShare(share.share_id)}
                          className="px-2.5 py-1 text-xs text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg hover:bg-red-500/20 transition"
                          data-testid={`revoke-share-${share.share_id}`}
                        >
                          Revoke
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Ended Shares */}
              {endedShares.length > 0 && (
                <div>
                  <h3 className="text-xs text-slate-500 uppercase tracking-wider mb-2">
                    Consent Ended ({endedShares.length})
                  </h3>
                  <div className="space-y-1">
                    {endedShares.map(share => (
                      <div key={share.share_id} className="bg-slate-800/50 rounded-lg p-2 flex items-center justify-between opacity-50" data-testid={`ended-share-${share.share_id}`}>
                        <span className="text-slate-500 text-xs">{share.viewer_name || 'Unknown'}</span>
                        <span className="text-slate-600 text-[10px]">
                          Ended {share.revoked_at ? new Date(share.revoked_at).toLocaleDateString('en-AU') : ''}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ConsentManagementModal;
